/**
 * The tag prop is required on web, so we need to add it in this file.
 */
import { forwardRef, Ref } from 'react';

import { TamaguiTextElement } from '@tamagui/core';

import { Text as NativeText } from './Text';
import type { TextProps as NativeTextProps } from './Text';

type Tag = Extract<
  keyof HTMLElementTagNameMap,
  | 'em'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'label'
  | 'p'
  | 'q'
  | 'small'
  | 'span'
  | 'strong'
  | 'time'
>;

export type TextProps = Omit<NativeTextProps, 'tag'> & {
  /**
   * Controls the rendered HTML tag on web (see https://developer.mozilla.org/en-US/docs/Web/HTML/Element)
   */
  tag: Tag;
};

/**
 * Basic component for rendering text. Supports most [Tamagui props](https://tamagui.dev/docs/intro/props), including:
 * - React Native [text](https://reactnative.dev/docs/text-style-props), [view](https://reactnative.dev/docs/view-style-props),
 * and [layout](https://reactnative.dev/docs/layout-props) props
 * - [React Native Web text props](https://necolas.github.io/react-native-web/docs/text/)
 * - Media query props like `$largerThanSM`
 *
 * `Text` does **not** support press handlers like `onPress`. For a11y reasons, you should only use interactive
 * components like `Button` to handle press interactions.
 *
 * The `tag` prop is required on web so that the correct HTML tag can be
 * rendered. Most of the time, you'll want to use `span` for inline text, `p`
 * for paragraphs, or `h1`-`h6` for headings. If you want to use an HTML tag
 * that isn't currently supported, please reach out so we can update the list
 * of supported tags.
 *
 * On web, `Text` defaults to `display: block` or `display: inline` depending
 * on the tag. This is so we can support text nested inside of other text. For
 * example, if you need to make a couple words in the middle of a paragraph a
 * different color, you could use a `span` tag inside of a `p` tag:
 *
 * ```tsx
 *  <Text typography="$bodyMedium" tag="p">
 *   Lorem ipsum dolor sit,{' '}
 *   <Text typography="$bodyMedium" tag="span" color="$contentAccentBrand">
 *   amet consectetur
 *   </Text>{' '}
 *   adipisicing elit.
 * </Text>
 * ```
 *
 * (On native, `display: block` and `display: inline` aren't available, but they
 * also aren't necessary for nesting text inside of other text, so `Text`
 * defaults to `display: flex` just like `Box` and other components.)
 */
export const Text = forwardRef(function Text(props: TextProps, ref: Ref<TamaguiTextElement>) {
  return <NativeText ref={ref} {...props} />;
});
